<template>
    <v-form ref="form" v-model="valid" data-cy="sales-settings" class="text-body-1">
        <v-row v-if="props.contractGroupId" no-gutters class="justify-end">
            <CopyContractModal
                v-if="salesSettings?.secondLevelInfoTabs"
                v-model="formData"
                :available-tabs="salesSettings?.secondLevelInfoTabs"
                @copied="save"
            />
        </v-row>
        <v-row dense class="mb-5">
            <v-col cols="12" sm="6" md="4" order="1" order-sm="0">
                <v-text-field
                    v-model="formData.name"
                    :label="`${$t('name')}*`"
                    data-cy="name"
                    hide-details="auto"
                    :rules="[requiredRule('name')]"
                />
            </v-col>
            <v-col cols="12" sm="6" md="8" order="0">
                <v-switch
                    :model-value="formData.status === 'ACTIVE'"
                    class="ml-auto mr-4 width-max-content"
                    :label="formData.status === 'ACTIVE' ? $t('active') : $t('inactive')"
                    data-cy="status"
                    hide-details
                    @update:model-value="changeStatus"
                />
            </v-col>
        </v-row>
        <v-checkbox
            v-model="formData.defaultContractGroup"
            :label="$t('product.info.default')"
            hide-details
            data-cy="default"
        />
        <SalesSettingsForm ref="salesSettings" v-model="formData" />
        <v-row class="pa-4 pt-10 gc-4">
            <v-btn :disabled="isLoading || !valid" data-cy="save" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn :disabled="isLoading" variant="text" data-cy="cancel" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {contractGroupGetToPutTransformer, contractGroupSaveTransformer} from '~/adapters/salesSettingsAdapter'
    import {getMappedProductKeys} from '~/utils/authorization-info-utils'

    const props = defineProps<{
        contractGroupId?: number
    }>()

    const emit = defineEmits<{
        (e: 'save' | 'cancel'): void
    }>()

    const {$api, $toast, $conf} = useNuxtApp()
    const {t} = useI18n()

    const isLoading = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const formData = ref({})
    const salesSettings = ref(null)
    const mappedProductKeys = ref([])

    provide('isShowInvoices', true)
    provide('isShowAdditionally', true)
    provide('isShowCalculationCommission', true)
    provide('contractGroupPlace', 'CONTRACT_GROUP')

    onMounted(async () => {
        mappedProductKeys.value = getMappedProductKeys($conf.public.activeProducts, authStore.availableProducts)
        if (props.contractGroupId) await loadData()
        else await initDefaultData()

        formData.value.charges.sort((a, b) => a.currency.localeCompare(b.currency))
    })

    async function initDefaultData(): Promise<void> {
        formData.value.status = 'ACTIVE'
        formData.value.name = null
        formData.value.defaultContractGroup = false

        formData.value.productTypeEnabled = mappedProductKeys.value.reduce((acc, prod) => {
            acc[prod] = true
            return acc
        }, {})

        formData.value.currency = authStore.systemCurrency || persistentStore.currency
        formData.value.pricingRuleRoot = {
            markupValue: 0,
            markupValueType: 'PERCENTAGE',
            commissionInCurrency: false,
            commissionValue: 0,
            ruleToken: 'rootRule',
            ruleValue: '',
            labelToken: 'rootRule',
            pricingRuleEntries: [],
            otherRule: false,
            ruleOperation: 'EQ',
        }
        formData.value.performSalesMode = 'SUPPLIER_CURRENCIES'
        formData.value.roundUpPrices = false
        formData.value.roundUpCommission = true
        formData.value.applyCommissionToCancelledService = false

        formData.value.serviceFeeCurrency = authStore.systemCurrency || persistentStore.currency
        formData.value.serviceFeeRuleRoot = {
            serviceFeeValue: 0,
            serviceFeeValueType: 'PERCENTAGE',
            ruleToken: 'rootRule',
            ruleValue: '',
            labelToken: 'rootRule',
            pricingRuleEntries: [],
            otherRule: false,
            ruleOperation: 'EQ',
        }

        formData.value.generalVatPlanId = null
        formData.value.commissionVatPlanId = null

        formData.value.daySales = mappedProductKeys.value.map(product => ({productType: product, period: 0}))
        formData.value.showPackageRates = true
        formData.value.voucherMode = 'PERMIT_AFTER_PAYMENT'
        formData.value.b2bPointOfSale = true
        formData.value.b2cPointOfSale = false
        if (runtimeStore.getCRMOption('asp.option.credit.limit.settings')) {
            formData.value.creditLimitSettings = [
                'BOOKED_AMOUNT_EXCEEDED',
                'INVOICED_AMOUNT_EXCEEDED',
                'BOOKED_AND_INVOICED_AMOUNT_EXCEEDED',
            ].map(creditLimitType => ({creditLimitExceeded: false, creditLimitType, creditLimitValue: null}))
        }
        formData.value.paymentMethodPlanId = null

        formData.value.charges = []
        Object.keys(runtimeStore.exchangeRates).forEach(key => formData.value.charges.push({currency: key, charge: 0}))

        formData.value.fines = mappedProductKeys.value.map(product => ({
            productType: product,
            period: 0,
            percent: 0,
            extendedPeriodDays: 0,
            markupPercent: 0,
            baseFine: 'ALL_COST',
        }))

        if (runtimeStore.getCRMOption('asp.option.disable.invoice.module')) {
            formData.value.manualInvoicesForFullAmount = false
        } else {
            formData.value.enableRequestInvoicesForCustomer = true
            formData.value.enableRequestInvoicesForAllServices = true
            if (runtimeStore.getCRMOption('asp.option.invoice.partial')) {
                formData.value.enablePartialInvoiceForCustomer = false
            }
            formData.value.enableEditVoucherAfterInvoicing = true
            if (runtimeStore.getCRMOption('asp.option.finance.event.rates.configuration.enabled')) {
                formData.value.applyCustomRatesSettings = false
                formData.value.currentRateForFinanceEvent = {
                    PAYMENT: false,
                    PENALTY: false,
                    MODIFY: false,
                    CANCEL: false,
                }
            }
        }

        formData.value.groupingProducts = mappedProductKeys.value.map(product => ({
            productType: product,
            bestPriceOffersFilterStrategy: 'ALL',
            flagGroupProducts: false,
            groupHeaderDisplayingStrategy: 'BEST_PRICE_OFFER_DESCRIPTION',
            offersWithBestProfitFilterEnabled: false,
            ownProductDynamicPriceAdjustment: null,
            ownProductDynamicPriceAdjustmentInCurrency: false,
            ownProductDynamicPriceDifference: null,
            ownProductDynamicPriceDifferenceInCurrency: false,
            ownProductDynamicPricingEnabled: false,
        }))

        formData.value.loyalties = []

        formData.value.ignoreTagsSettingsForTO1 = false
        formData.value.requestedTags = 'public'
        formData.value.excludedTags = ''

        formData.value.enableUserModification = true
        formData.value.enableAgencyModification = true
        formData.value.allowToConfigureOwnPaymentMethods = false
        if (runtimeStore.getCRMOption('asp.option.enable.customFields')) {
            formData.value.enableUserCustomFieldModification = true
        }
        if (runtimeStore.getCRMOption('asp.option.enable.business.trip.documents.generation')) {
            formData.value.enableBusinessTripDocumentsGeneration = false
        }
        if (runtimeStore.getCRMOption('asp.option.enable.contract.settings.for.attach.docs.for.corporator')) {
            formData.value.validateContractDocs = false
        }
    }

    async function loadData(): Promise<void> {
        isLoading.value = true
        try {
            const res = await $api.contractGroup.get({contractGroupId: props.contractGroupId})
            formData.value = contractGroupGetToPutTransformer(res)
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        let contract = clone(formData.value)
        contract = await contractGroupSaveTransformer(contract)
        isLoading.value = true
        try {
            if (props.contractGroupId) {
                await $api.contractGroup.put(contract)
            } else {
                await $api.contractGroup.post(contract)
            }
            $toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            $toast.error(e.msg || e.errors[0].message || t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const changeStatus = () => {
        formData.value.status = formData.value.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    }
</script>
